import { createFlow } from '@wix/gg-components/orchestrator';
import { StepId, ActionId } from 'config/steps';
import { StepParams, StepRenderPart } from 'types/steps';
import { WelcomeStep } from 'steps/welcome/WelcomeStep';
import { CategoryContent } from 'steps/category/CategoryContent';
import { CategoryPreview } from 'steps/category/CategoryPreview';
import { StoreNameContent } from 'steps/storeName/StoreNameContent';
import { StoreNamePreview } from 'steps/storeName/StoreNamePreview';
import { LaunchStoreContent } from 'steps/launchStore/LaunchStoreContent';
import { LaunchStorePreview } from 'steps/launchStore/LaunchStorePreview';
import { welcomeNextAction, categoryNextAction, templateNextAction, storeNameNextAction } from '../../actions';
import { SetupStoreContent } from 'steps/setupStore/SetupStoreContent';
import { SetupStorePreview } from 'steps/setupStore/SetupStorePreview';
import { getInitialCategory, getInitialStoreName, getInitialThemeKey, getIsMinimalFlow } from 'config/client';
import { getProviderConfig, getSubIdConfig } from 'config/providers';
import { ThemeContent } from 'steps/theme/ThemeContent';
import { ThemePreview } from 'steps/theme/ThemePreview';
import { SitePreview } from 'steps/sitePreview/SitePreview';
import { MinimalSetupStep } from 'steps/minimalSetup/MinimalSetupStep';
import { useIsMobile } from '@wix/gg-common-lib/hooks';

export function useFlowBuilder() {
  const isMobile = useIsMobile();
  const flow = createFlow<StepParams, StepRenderPart>();

  if (getIsMinimalFlow()) {
    flow.step(StepId.MinimalSetup, MinimalSetupStep, { fullScreen: true, countInStepper: false });
    return flow.build();
  }

  const providerConfig = getProviderConfig();
  const { skipWelcomeStep } = providerConfig;
  const skipCategory = !!getInitialCategory();
  const skipTheme = !!getInitialThemeKey();
  const skipStoreName = !!getInitialStoreName();

  const showWelcome = !skipCategory && (!skipWelcomeStep || !!getSubIdConfig()?.showWelcome);

  if (showWelcome) {
    flow.step(StepId.Welcome, WelcomeStep, { fullScreen: true, countInStepper: false });
    flow.action(ActionId.WelcomeNext, welcomeNextAction);
  }
  if (!skipCategory) {
    flow.step(StepId.Category, { main: CategoryContent, preview: CategoryPreview }, { splitRatio: 30 });
    flow.action(ActionId.CategoryNext, categoryNextAction);
  }
  if (!skipTheme) {
    flow.step(StepId.Theme, { main: ThemeContent, preview: ThemePreview }, { splitRatio: 30 });
    flow.action(ActionId.ThemeNext, templateNextAction);
  }
  flow.step(
    StepId.SetupStore,
    { main: SetupStoreContent, preview: SetupStorePreview },
    { fullScreen: true, countInStepper: false },
  );
  if (!skipStoreName) {
    flow.step(
      StepId.StoreName,
      { main: StoreNameContent, preview: StoreNamePreview },
      { splitRatio: 30, checkpoint: true },
    );
    flow.action(ActionId.StoreNameNext, storeNameNextAction);
  }
  flow.step(
    StepId.LaunchStore,
    { main: LaunchStoreContent, preview: LaunchStorePreview },
    { splitRatio: 30, countInStepper: false },
  );

  const component = isMobile ? SitePreview : { preview: SitePreview };
  flow.step(StepId.SitePreview, component, { countInStepper: false, splitRatio: 0 });

  return flow.build();
}
